import React, { useState, useEffect, useCallback } from 'react';
import { auditApi } from '../api';
import JsonViewer from '../shared/JsonViewer';

export default function AuditTab({ workspaceId, workspaceName }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const data = await auditApi.list(workspaceId);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleExpand = async (entry) => {
    if (expanded === entry.audit_id) { setExpanded(null); return; }
    if (entry.execution_id) {
      try {
        const d = await auditApi.getProvenance(entry.execution_id);
        setDetail(d);
      } catch { setDetail(entry); }
    } else {
      setDetail(entry);
    }
    setExpanded(entry.audit_id);
  };

  const filtered = search
    ? entries.filter(e => JSON.stringify(e).toLowerCase().includes(search.toLowerCase()))
    : entries;

  return (
    <div>
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Audit Trail</h2>
          <button className="ednl-btn ednl-btn--sm" onClick={load}>Refresh</button>
        </div>

        <div style={{ color: 'var(--uh-text-secondary)', marginBottom: 8 }}>
          Showing entries for workspace{' '}
          <strong>{workspaceName || '(name unknown)'}</strong>{' '}
          <code className="mono">{workspaceId || '(none selected)'}</code>. Each EDNL run that
          closes a frame and triggers a process produces one row. Click <strong>Provenance</strong>{' '}
          on a row to see source events, narratives, aggregate hits, and the{' '}
          <code>execution_id</code>.
        </div>

        <div style={{ marginBottom: 12 }}>
          <input
            className="ednl-input"
            placeholder="Filter (optional) - leave blank to see all entries"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </div>

        {loading ? <div className="ednl-loading">Loading...</div> : filtered.length === 0 ? (
          <div className="ednl-empty">
            <div className="ednl-empty__icon">🔍</div>
            <div className="ednl-empty__text">
              No audit entries
              {search ? ' matching your filter' : (
                <>
                  {' '}for workspace{' '}
                  <strong>{workspaceName || '(name unknown)'}</strong>{' '}
                  <code className="mono">{workspaceId || '(none)'}</code>
                </>
              )}.
            </div>
            {!search && (
              <div style={{ marginTop: 8, color: 'var(--uh-text-secondary)', textAlign: 'left', maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
                <strong>Empty list?</strong> Audit entries are scoped to <code>workspace_id</code>.
                If you ran the EDNL Demo with a different workspace selected (or with none selected,
                which falls back to <code>'default'</code>), the entry exists but lives under a
                different id. Either change the workspace in the picker above, or check the DB:
                <pre style={{ background: '#f9fafb', padding: 8, borderRadius: 4, marginTop: 6, whiteSpace: 'pre-wrap' }}>
{`docker exec cm-postgres psql -U admin -d cerebralmachines \\
  -c "SELECT \\"Timestamp\\", \\"Data\\"->>'workspace_id' AS ws, \\"Data\\"->>'execution_id' AS execution_id FROM \\"CerebralMachines\\".\\"TraceEvent\\" WHERE \\"Phase\\"='ednl_audit' ORDER BY \\"Timestamp\\" DESC LIMIT 5;"`}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead><tr><th>Audit ID</th><th>Execution</th><th>Frame</th><th>Process</th><th>Events</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {filtered.map(e => (
                  <React.Fragment key={e.audit_id}>
                    <tr>
                      <td className="mono">{e.audit_id?.slice(0, 8)}...</td>
                      <td className="mono">{e.execution_id?.slice(0, 8) || '—'}...</td>
                      <td className="mono">{e.frame_id?.slice(0, 8) || '—'}...</td>
                      <td className="mono">{e.process_id?.slice(0, 8) || '—'}...</td>
                      <td>{e.source_event_ids?.length || 0}</td>
                      <td>{e.created_at?.slice(0, 19)}</td>
                      <td>
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleExpand(e)}>
                          {expanded === e.audit_id ? 'Hide' : 'Provenance'}
                        </button>
                      </td>
                    </tr>
                    {expanded === e.audit_id && detail && (
                      <tr className="ednl-expand-row">
                        <td colSpan={7}>
                          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                            <ProvenanceSection title="Source Events" items={detail.source_event_ids} />
                            <ProvenanceSection title="Narrative IDs" items={detail.narrative_ids} />
                            <ProvenanceSection title="Aggregate Hits" items={detail.aggregate_hit_ids} />
                          </div>
                          {detail.composite_narrative && (
                            <div style={{ marginTop: 12 }}>
                              <strong style={{ fontSize: 12 }}>Composite Narrative:</strong>
                              <div style={{ background: '#f9fafb', padding: 12, borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap', marginTop: 4 }}>
                                {detail.composite_narrative}
                              </div>
                            </div>
                          )}
                          <JsonViewer data={detail} label="Full audit entry" />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ProvenanceSection({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <strong className="ednl-hint">{title} ({items.length})</strong>
      <div style={{ marginTop: 4 }}>
        {items.map((id, i) => (
          <div key={i} className="mono" style={{ padding: '2px 0', color: '#4b5563' }}>{id}</div>
        ))}
      </div>
    </div>
  );
}
